import { addDays, compareOpportunities, localDate, startMinute, type Opportunity } from '../../shared/opportunity';

/**
 * The Calendar layout's month: the grid's days, and the events sorted into them.
 * Pure, so the layout maths can be tested without drawing anything.
 */

/** A calendar month: `month` is 0 for January, as `Date` counts. */
export interface Month {
  year: number;
  month: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

export const monthOf = (dayKey: string): Month => ({
  year: Number(dayKey.slice(0, 4)),
  month: Number(dayKey.slice(5, 7)) - 1,
});

export const monthKey = ({ year, month }: Month): string => `${year}-${pad(month + 1)}`;

export const inMonth = (dayKey: string, m: Month): boolean => dayKey.startsWith(monthKey(m));

export function shiftMonth({ year, month }: Month, delta: number): Month {
  const n = year * 12 + month + delta;
  return { year: Math.floor(n / 12), month: ((n % 12) + 12) % 12 };
}

/** "September 2026" */
export const monthTitle = ({ year, month }: Month): string =>
  new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** "September" */
export const monthName = ({ year, month }: Month): string =>
  new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });

export const WEEKS = 6;
/** Sunday first, as US calendars are. */
export const WEEKDAY_HEADS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Six weeks of seven day keys, starting on the Sunday on or before the 1st.
 * Always six, so the grid keeps its height from one month to the next.
 */
export function monthGrid(year: number, month: number): string[][] {
  const first = `${year}-${pad(month + 1)}-01`;
  const lead = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const start = addDays(first, -lead);
  return Array.from({ length: WEEKS }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
}

/** Each day's events, in the order the day runs: all-day first, then by start. */
export function bucketByDay(events: Iterable<Opportunity>): Map<string, Opportunity[]> {
  const days = new Map<string, Opportunity[]>();
  for (const event of events) {
    const key = localDate(event);
    const list = days.get(key);
    if (list) list.push(event);
    else days.set(key, [event]);
  }
  for (const list of days.values()) list.sort(compareOpportunities);
  return days;
}

/** Chips a cell shows before it folds the rest into "+N more". */
export const CELL_CHIPS = 3;

/** What a cell draws folded: every chip when they fit, otherwise the first three and a count. */
export function foldCell<T>(items: T[], expanded: boolean): { shown: T[]; more: number } {
  if (expanded || items.length <= CELL_CHIPS) return { shown: items, more: 0 };
  return { shown: items.slice(0, CELL_CHIPS), more: items.length - CELL_CHIPS };
}

/** "10a", "10:30a", "All day": a chip's time, short enough to leave room for the name. */
export function chipTime(event: Opportunity): string {
  if (event.isAllDay) return 'All day';
  const minutes = startMinute(event);
  if (minutes == null) return '';
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}${m ? `:${pad(m)}` : ''}${h24 < 12 ? 'a' : 'p'}`;
}

/**
 * The day a month opens on, on a phone: today if it has events, else the first
 * day from today on that does, else the month's first day with any, else today
 * (when it is in the month) or the 1st.
 */
export function defaultDay(m: Month, today: string, days: ReadonlyMap<string, unknown[]>): string {
  const busy = [...days.keys()].filter((k) => inMonth(k, m) && days.get(k)!.length).sort();
  if (busy.includes(today)) return today;
  const next = busy.find((k) => k >= today);
  if (next) return next;
  if (busy.length) return busy[0];
  return inMonth(today, m) ? today : `${monthKey(m)}-01`;
}

/** The nearest month after (or else before) `m` with an event in it, for the empty month's pointer. */
export function nearestBusyMonth(m: Month, days: ReadonlyMap<string, unknown[]>): Month | null {
  const here = monthKey(m);
  const months = [...new Set([...days.keys()].map((k) => k.slice(0, 7)))].sort();
  const key = months.find((k) => k > here) ?? months.reverse().find((k) => k < here);
  return key ? monthOf(`${key}-01`) : null;
}
